import { useEffect, useId, useState } from "react";
import { ExpandableMedia } from "./ExpandableMedia";

let mermaidPromise: Promise<(typeof import("mermaid"))["default"]> | undefined;
let mermaidRenderSequence = 0;
const renderedDiagrams = new Map<string, string>();

function loadMermaid() {
  mermaidPromise ??= import("mermaid").then(({ default: mermaid }) => {
    mermaid.initialize({
      startOnLoad: false,
      suppressErrorRendering: true,
      securityLevel: "strict",
      theme: "neutral",
      themeVariables: {
        primaryColor: "#f2f8f4",
        primaryBorderColor: "#b9d8c4",
        primaryTextColor: "#26332c",
        lineColor: "#718077",
        secondaryColor: "#f7f8f6",
        tertiaryColor: "#ffffff",
      },
    });
    return mermaid;
  });
  return mermaidPromise;
}

export function MermaidDiagram({ expandable = true, source }: { expandable?: boolean; source: string }) {
  const id = useId().replaceAll(":", "");
  const [svg, setSvg] = useState<string | undefined>(() => renderedDiagrams.get(source));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = renderedDiagrams.get(source);
    if (cached) {
      setSvg(cached);
      setFailed(false);
      return () => { cancelled = true; };
    }
    const renderId = `diagram-${id}-${mermaidRenderSequence += 1}`;
    setSvg(undefined);
    setFailed(false);
    void loadMermaid()
      .then((mermaid) => mermaid.render(renderId, source))
      .then(({ svg: rendered }) => {
        renderedDiagrams.set(source, rendered);
        if (!cancelled) setSvg(rendered);
      })
      .catch((error: unknown) => {
        console.error("CHEW could not render a Mermaid diagram", error);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, source]);

  if (failed) return <pre className="diagram-fallback">{source}</pre>;
  if (!svg) return <div className="diagram-loading">Drawing the system…</div>;
  const diagram = <div className="mermaid-diagram" dangerouslySetInnerHTML={{ __html: svg }} />;
  return expandable
    ? <ExpandableMedia className="expandable-diagram" label="diagram">{diagram}</ExpandableMedia>
    : diagram;
}
